import { vnTemplate } from '../../../../vendor/valnet/valnet_jslib/vnKnockoutJsTemplates/vnTemplate';
import { listPageSelect_template } from '~/components/templates/listPageSelect_template';
import { html, LpButton, LpEditCard, LpEditGrid, LpEditSectionTitle, LpEditSteps, LpEditTabs, LpFormField, LpIcon, LpPageHeader, raw } from '~/design-system';
import { getTransmissionLiveStatusOption } from '~/utils/transmissionLiveStatus';
const YES_NO_OPTIONS = [
    { id: '0', name: 'No' },
    { id: '1', name: 'Yes' },
];
const LIVE_OPTIONS = [
    getTransmissionLiveStatusOption({ id: '1', name: 'Live' }),
    getTransmissionLiveStatusOption({ id: '0', name: 'Not Live' }),
];
export class adminTransmissionEdit_template extends vnTemplate {
    constructor() {
        super(...arguments);
        this.sections = [
            {
                id: 'basics',
                label: 'Basics',
                icon: 'sliders-horizontal',
                title: 'Basic configuration',
                description: 'Name, channel, agency, transmission type, and live status.',
            },
            {
                id: 'targeting',
                label: 'Targeting',
                icon: 'target',
                title: 'Lead targeting',
                description: 'Partners, scheduling groups, SQL, and tokenized parameters.',
            },
            {
                id: 'preset',
                label: 'Customization',
                icon: 'magic-wand',
                title: 'Customization preset',
                description: 'Optional Groovy preset applied to this transmission type.',
            },
            {
                id: 'file',
                label: 'File format',
                icon: 'file',
                title: 'File format',
                description: 'Filename, delimiters, headers, and compression settings.',
            },
            {
                id: 'report',
                label: 'Report',
                icon: 'chart-bar',
                title: 'Report format',
                description: 'Report type, template, and delivery flags.',
            },
            {
                id: 'http',
                label: 'HTTP',
                icon: 'globe',
                title: 'HTTP delivery',
                description: 'Endpoint, security, and response matching settings.',
            },
            {
                id: 'email',
                label: 'Email',
                icon: 'envelope',
                title: 'Email delivery',
                description: 'Sender, recipients, subject, and message content.',
            },
            {
                id: 'security',
                label: 'FTP & PGP',
                icon: 'lock',
                title: 'FTP & PGP',
                description: 'Secure file transfer and encryption settings.',
            },
            {
                id: 'audit',
                label: 'Audit',
                icon: 'clock-counter-clockwise',
                title: 'Audit information',
                description: 'Read-only metadata for this transmission.',
            },
        ];
    }
    /**
     * Return the template key used by the Valnet template framework.
     *
     * @return string
     */
    getTemplateName() {
        return 'adminTransmissionEdit-template';
    }
    /**
     * Return the CSS classes applied to the template root element.
     *
     * @return Array<string>
     */
    getMainElementClasses() {
        return ['w-content'];
    }
    /**
     * Build the Knockout view model used by this template.
     *
     * @return any
     */
    buildViewModel() {
        const self = this;
        return {
            id: ko.observable(0),
            name: ko.observable(''),
            channel: ko.observable(''),
            transmissionAgencyId: ko.observable(''),
            partnerIdsText: ko.observable(''),
            transmissionType: ko.observable(''),
            groupIdsText: ko.observable(''),
            live: ko.observable('1'),
            sqlstatement: ko.observable(''),
            tokenizedParameters: ko.observable(''),
            maxLeadsPerTransmission: ko.observable(''),
            groovySource: ko.observable('NONE'),
            sftpEnabled: ko.observable('0'),
            ftpHost: ko.observable(''),
            ftpUsername: ko.observable(''),
            ftpPass: ko.observable(''),
            ftpDir: ko.observable(''),
            gpgEnabled: ko.observable('0'),
            gpgKeyringPassword: ko.observable(''),
            gpgOutputFilename: ko.observable(''),
            gpgSignWith: ko.observable(''),
            reportType: ko.observable(''),
            reportTemplateFile: ko.observable(''),
            trackChanges: ko.observable('0'),
            forceSendingEmail: ko.observable('0'),
            filename: ko.observable(''),
            fileExtension: ko.observable(''),
            fieldDelimiter: ko.observable(''),
            eolDelimiter: ko.observable(''),
            showHeaders: ko.observable('0'),
            zipEnabled: ko.observable('0'),
            fileHeader: ko.observable(''),
            fileFooter: ko.observable(''),
            isSecure: ko.observable('0'),
            sendLeadId: ko.observable('0'),
            isEncoded: ko.observable('0'),
            hostname: ko.observable(''),
            hostpath: ko.observable(''),
            port: ko.observable(''),
            successStrings: ko.observable(''),
            errorStrings: ko.observable(''),
            httpRequestHeaders: ko.observable(''),
            emailFrom: ko.observable(''),
            emailTo: ko.observable(''),
            emailCc: ko.observable(''),
            emailBcc: ko.observable(''),
            subject: ko.observable(''),
            contentType: ko.observable(''),
            contentMessage: ko.observable(''),
            comments: ko.observable(''),
            lastUpdatedDate: ko.observable(''),
            lastUpdatedBy: ko.observable(''),
            types: ko.observableArray([]),
            channels: ko.observableArray([]),
            agencies: ko.observableArray([]),
            groups: ko.observableArray([]),
            partners: ko.observableArray([]),
            customizationPresets: ko.observableArray([]),
            gpgRecipients: ko.observableArray([]),
            sections: this.sections,
            activeSection: ko.observable('basics'),
            onClickSave: function () { self.caller.saveTransmission(); },
            onClickRun: function () { self.caller.runTransmission(); },
            onClickBack: function () { self.caller.backToTransmissions(); },
            onClickSection: function (section) { self.selectSection(section.id); },
            onClickPrevious: function () { self.goToPreviousSection(); },
            onClickNext: function () { self.goToNextSection(); },
        };
    }
    /**
     * Register computed view-model properties after the template VM is assigned.
     *
     * @return void
     */
    initPartial() {
        const self = this;
        const vm = this.getTemplateViewModel();
        this.channelSelect = this.createFormSelect('adminTransmissionChannelSelect', 'Channel', vm.channel, [
            { id: '', name: 'Select channel' },
        ]);
        this.agencySelect = this.createFormSelect('adminTransmissionAgencySelect', 'Agency', vm.transmissionAgencyId, [
            { id: '', name: 'Select agency' },
        ]);
        this.typeSelect = this.createFormSelect('adminTransmissionTypeSelect', 'Type', vm.transmissionType, [
            { id: '', name: 'Select type' },
        ]);
        this.liveSelect = this.createFormSelect('adminTransmissionLiveSelect', 'Status', vm.live, LIVE_OPTIONS);
        this.presetSelect = this.createFormSelect('adminTransmissionPresetSelect', 'Preset', vm.groovySource);
        this.showHeadersSelect = this.createFormSelect('adminTransmissionShowHeadersSelect', 'Show Headers', vm.showHeaders, YES_NO_OPTIONS);
        this.zipEnabledSelect = this.createFormSelect('adminTransmissionZipEnabledSelect', 'Zip Enabled', vm.zipEnabled, YES_NO_OPTIONS);
        this.trackChangesSelect = this.createFormSelect('adminTransmissionTrackChangesSelect', 'Track Changes', vm.trackChanges, YES_NO_OPTIONS);
        this.forceSendingEmailSelect = this.createFormSelect('adminTransmissionForceSendingEmailSelect', 'Process If Zero Leads', vm.forceSendingEmail, YES_NO_OPTIONS);
        this.isSecureSelect = this.createFormSelect('adminTransmissionIsSecureSelect', 'Secure', vm.isSecure, YES_NO_OPTIONS);
        this.sendLeadIdSelect = this.createFormSelect('adminTransmissionSendLeadIdSelect', 'Send Lead ID', vm.sendLeadId, YES_NO_OPTIONS);
        this.isEncodedSelect = this.createFormSelect('adminTransmissionIsEncodedSelect', 'Encoded', vm.isEncoded, YES_NO_OPTIONS);
        this.sftpEnabledSelect = this.createFormSelect('adminTransmissionSftpEnabledSelect', 'FTP Enabled', vm.sftpEnabled, YES_NO_OPTIONS);
        this.gpgEnabledSelect = this.createFormSelect('adminTransmissionGpgEnabledSelect', 'PGP Enabled', vm.gpgEnabled, YES_NO_OPTIONS);
        vm.selectedTransmissionTypeDescription = ko.pureComputed(function () {
            const selectedType = String(vm.transmissionType() || '');
            const match = (vm.types() || []).find((type) => String(type.id) === selectedType);
            return match && match.description ? match.description : '';
        });
        vm.availableCustomizationPresets = ko.pureComputed(function () {
            const selectedType = String(vm.transmissionType() || '');
            return (vm.customizationPresets() || []).filter((preset) => {
                const supportedTypes = preset.supportedTypes || [];
                return supportedTypes.length === 0
                    || supportedTypes.some((type) => String(type) === selectedType);
            });
        });
        vm.selectedCustomizationPresetDescription = ko.pureComputed(function () {
            const selectedPreset = String(vm.groovySource() || 'NONE');
            const match = (vm.customizationPresets() || []).find((preset) => String(preset.id) === selectedPreset);
            return match && match.description ? match.description : '';
        });
        vm.visibleSections = ko.pureComputed(function () {
            return self.getVisibleSections();
        });
        vm.sectionCompletion = ko.pureComputed(function () {
            return self.getSectionCompletion();
        });
        vm.availableCustomizationPresets.subscribe(function (presets) {
            self.presetSelect.setOptions(self.toSelectOptions(presets));
        });
        vm.isFirstSection = ko.pureComputed(function () {
            return self.getSectionIndex(vm.activeSection()) === 0;
        });
        vm.isLastSection = ko.pureComputed(function () {
            const ids = self.getVisibleSectionIds();
            return self.getSectionIndex(vm.activeSection()) === ids.length - 1;
        });
        ko.computed(function () {
            return vm.transmissionType();
        }).subscribe(function () {
            self.ensureActiveSectionVisible();
        });
        ko.computed(function () {
            return vm.id();
        }).subscribe(function () {
            self.ensureActiveSectionVisible();
        });
        vm.channel.subscribe(function (channel) {
            if (typeof self.caller.loadAgencies === 'function') {
                self.caller.loadAgencies(channel);
            }
        });
    }
    /**
     * Return the HTML markup for this template.
     *
     * @return string
     */
    getTemplateHtml() {
        return `
            ${LpPageHeader({
            title: html `<span data-bind="text: id() ? 'Edit Transmission' : 'New Transmission'"></span>`,
            description: 'Configure lead transmission delivery, scheduling, formatting, and security.',
            icon: 'paper-plane-tilt',
            actions: html `
                    ${LpButton({
                label: 'Back',
                prefixIcon: 'arrow-left',
                size: 'base',
                variant: 'secondary',
                attributes: {
                    'data-bind': 'click: onClickBack',
                },
            })}
                    ${LpButton({
                label: 'Run Now',
                prefixIcon: 'play',
                size: 'base',
                variant: 'secondary',
                attributes: {
                    'data-bind': 'event: { click: onClickRun }, visible: id',
                },
            })}
                    ${LpButton({
                label: 'Save',
                prefixIcon: 'check',
                size: 'base',
                variant: 'primary',
                attributes: {
                    'data-bind': 'click: onClickSave',
                },
            })}
                `,
        })}

            <div class="edit-page">
                ${LpEditTabs({
            ariaLabel: 'Transmission sections',
            itemsBinding: 'visibleSections',
        })}

                <div class="contract-edit-panel">
                ${LpEditCard({
            title: 'Basic configuration',
            description: 'Name, channel, agency, transmission type, and live status.',
            attributes: {
                'data-bind': "visible: activeSection() === 'basics'",
            },
            children: LpEditGrid({
                children: html `
                            ${LpEditSectionTitle({ title: 'Basic Information' })}
                            ${LpFormField({
                    label: 'Label / Name',
                    required: true,
                    children: raw('<input class="edit-input" type="text" placeholder="Transmission name" data-bind="value: name" />'),
                })}
                            ${LpFormField({
                    label: 'Channel',
                    children: raw(this.channelSelect.getHtmlBuild()),
                })}
                            ${LpFormField({
                    label: 'Agency',
                    children: raw(this.agencySelect.getHtmlBuild()),
                })}
                            ${LpFormField({
                    label: 'Status',
                    children: raw(this.liveSelect.getHtmlBuild()),
                })}
                            ${LpEditSectionTitle({ title: 'Transmission:' })}
                            ${LpFormField({
                    label: 'Transmission Type',
                    required: true,
                    children: html `
                                    ${raw(this.typeSelect.getHtmlBuild())}
                                    <div class="admin-transmission-type-description" data-bind="visible: selectedTransmissionTypeDescription">
                                        <span class="admin-transmission-type-description__eyebrow">
                                            ${LpIcon({ name: 'info', size: 14 })}
                                            <span>How it works</span>
                                        </span>
                                        <span data-bind="text: selectedTransmissionTypeDescription"></span>
                                    </div>
                                `,
                })}
                            ${LpFormField({
                    label: 'Max Leads Per Transmission',
                    children: raw('<input class="edit-input" type="number" data-bind="value: maxLeadsPerTransmission" />'),
                })}
                        `,
            }),
        })}

                ${LpEditCard({
            title: 'Lead targeting',
            description: 'Partners, scheduling groups, SQL, and tokenized parameters.',
            attributes: {
                'data-bind': "visible: activeSection() === 'targeting'",
            },
            children: LpEditGrid({
                children: html `
                            ${LpFormField({
                    label: 'Partner IDs',
                    className: 'edit-span',
                    children: html `
                                    ${raw('<input class="edit-input" type="text" placeholder="Comma-separated partner IDs" data-bind="value: partnerIdsText" />')}
                                    <div class="admin-transmission-muted" data-bind="text: partners().length ? partners().length + ' available partners loaded' : ''"></div>
                                `,
                })}
                            ${LpFormField({
                    label: 'Scheduling Group IDs (GMT time)',
                    className: 'edit-span',
                    children: html `
                                    ${raw('<input class="edit-input" type="text" placeholder="Comma-separated scheduling group IDs" data-bind="value: groupIdsText" />')}
                                    <div class="admin-transmission-muted" data-bind="foreach: groups"><span data-bind="text: id + ': ' + label"></span><span> </span></div>
                                `,
                })}
                            ${LpFormField({
                    label: 'SQL Statement',
                    className: 'edit-span admin-transmission-code',
                    children: raw('<textarea class="edit-input edit-textarea" data-bind="value: sqlstatement"></textarea>'),
                })}
                            ${LpFormField({
                    label: 'Tokenized Parameters',
                    className: 'edit-span',
                    children: raw('<textarea class="edit-input edit-textarea" data-bind="value: tokenizedParameters"></textarea>'),
                })}
                        `,
            }),
        })}

                ${LpEditCard({
            title: 'Customization preset',
            description: 'Optional Groovy preset applied to this transmission type.',
            attributes: {
                'data-bind': "visible: activeSection() === 'preset'",
            },
            children: LpEditGrid({
                children: LpFormField({
                    label: 'Preset',
                    className: 'edit-span',
                    children: html `
                                ${raw(this.presetSelect.getHtmlBuild())}
                                <div class="admin-transmission-type-description" data-bind="visible: selectedCustomizationPresetDescription, text: selectedCustomizationPresetDescription"></div>
                            `,
                }),
            }),
        })}

                ${LpEditCard({
            title: 'File format',
            description: 'Filename, delimiters, headers, and compression settings.',
            attributes: {
                'data-bind': "visible: activeSection() === 'file'",
            },
            children: LpEditGrid({
                children: html `
                            ${LpFormField({
                    label: 'Filename',
                    children: raw('<input class="edit-input" type="text" data-bind="value: filename" />'),
                })}
                            ${LpFormField({
                    label: 'Extension',
                    children: raw('<input class="edit-input" type="text" data-bind="value: fileExtension" />'),
                })}
                            ${LpFormField({
                    label: 'Field Delimiter',
                    children: raw('<input class="edit-input" type="text" data-bind="value: fieldDelimiter" />'),
                })}
                            ${LpFormField({
                    label: 'EOL Delimiter',
                    children: raw('<input class="edit-input" type="text" data-bind="value: eolDelimiter" />'),
                })}
                            ${LpFormField({
                    label: 'Show Headers',
                    children: raw(this.showHeadersSelect.getHtmlBuild()),
                })}
                            ${LpFormField({
                    label: 'Zip Enabled',
                    children: raw(this.zipEnabledSelect.getHtmlBuild()),
                })}
                            ${LpFormField({
                    label: 'File Header',
                    className: 'edit-span',
                    children: raw('<textarea class="edit-input edit-textarea" data-bind="value: fileHeader"></textarea>'),
                })}
                            ${LpFormField({
                    label: 'File Footer',
                    className: 'edit-span',
                    children: raw('<textarea class="edit-input edit-textarea" data-bind="value: fileFooter"></textarea>'),
                })}
                        `,
            }),
        })}

                ${LpEditCard({
            title: 'Report format',
            description: 'Report type, template, and delivery flags.',
            attributes: {
                'data-bind': "visible: activeSection() === 'report'",
            },
            children: LpEditGrid({
                children: html `
                            ${LpFormField({
                    label: 'Report Type',
                    children: raw('<input class="edit-input" type="text" data-bind="value: reportType" />'),
                })}
                            ${LpFormField({
                    label: 'Report Template',
                    children: raw('<input class="edit-input" type="text" data-bind="value: reportTemplateFile" />'),
                })}
                            ${LpFormField({
                    label: 'Track Changes',
                    children: raw(this.trackChangesSelect.getHtmlBuild()),
                })}
                            ${LpFormField({
                    label: 'Process If Zero Leads',
                    children: raw(this.forceSendingEmailSelect.getHtmlBuild()),
                })}
                        `,
            }),
        })}

                ${LpEditCard({
            title: 'HTTP delivery',
            description: 'Endpoint, security, and response matching settings.',
            attributes: {
                'data-bind': "visible: activeSection() === 'http'",
            },
            children: LpEditGrid({
                children: html `
                            ${LpFormField({ label: 'Secure', children: raw(this.isSecureSelect.getHtmlBuild()) })}
                            ${LpFormField({ label: 'Send Lead ID', children: raw(this.sendLeadIdSelect.getHtmlBuild()) })}
                            ${LpFormField({ label: 'Encoded', children: raw(this.isEncodedSelect.getHtmlBuild()) })}
                            ${LpFormField({ label: 'Hostname', children: raw('<input class="edit-input" type="text" data-bind="value: hostname" />') })}
                            ${LpFormField({ label: 'Host Path', children: raw('<input class="edit-input" type="text" data-bind="value: hostpath" />') })}
                            ${LpFormField({ label: 'Port', children: raw('<input class="edit-input" type="number" data-bind="value: port" />') })}
                            ${LpFormField({ label: 'Success Strings', className: 'edit-span', children: raw('<textarea class="edit-input edit-textarea" data-bind="value: successStrings"></textarea>') })}
                            ${LpFormField({ label: 'Error Strings', className: 'edit-span', children: raw('<textarea class="edit-input edit-textarea" data-bind="value: errorStrings"></textarea>') })}
                            ${LpFormField({ label: 'HTTP Headers', className: 'edit-span', children: raw('<textarea class="edit-input edit-textarea" data-bind="value: httpRequestHeaders"></textarea>') })}
                        `,
            }),
        })}

                ${LpEditCard({
            title: 'Email delivery',
            description: 'Sender, recipients, subject, and message content.',
            attributes: {
                'data-bind': "visible: activeSection() === 'email'",
            },
            children: LpEditGrid({
                children: html `
                            ${LpFormField({ label: 'From', children: raw('<input class="edit-input" type="text" data-bind="value: emailFrom" />') })}
                            ${LpFormField({ label: 'To', children: raw('<input class="edit-input" type="text" data-bind="value: emailTo" />') })}
                            ${LpFormField({ label: 'CC', children: raw('<input class="edit-input" type="text" data-bind="value: emailCc" />') })}
                            ${LpFormField({ label: 'BCC', children: raw('<input class="edit-input" type="text" data-bind="value: emailBcc" />') })}
                            ${LpFormField({ label: 'Subject', children: raw('<input class="edit-input" type="text" data-bind="value: subject" />') })}
                            ${LpFormField({ label: 'Content Type', children: raw('<input class="edit-input" type="text" data-bind="value: contentType" />') })}
                            ${LpFormField({ label: 'Content Message', className: 'edit-span', children: raw('<textarea class="edit-input edit-textarea" data-bind="value: contentMessage"></textarea>') })}
                            ${LpFormField({ label: 'Lead Team Comments', className: 'edit-span', children: raw('<textarea class="edit-input edit-textarea" data-bind="value: comments"></textarea>') })}
                        `,
            }),
        })}

                ${LpEditCard({
            title: 'FTP & PGP',
            description: 'Secure file transfer and encryption settings.',
            attributes: {
                'data-bind': "visible: activeSection() === 'security'",
            },
            children: html `
                        ${LpEditGrid({
                children: html `
                                ${LpFormField({ label: 'FTP Enabled', children: raw(this.sftpEnabledSelect.getHtmlBuild()) })}
                                ${LpFormField({ label: 'FTP Host', children: raw('<input class="edit-input" type="text" data-bind="value: ftpHost" />') })}
                                ${LpFormField({ label: 'FTP Username', children: raw('<input class="edit-input" type="text" data-bind="value: ftpUsername" />') })}
                                ${LpFormField({ label: 'FTP Password', children: raw('<input class="edit-input" type="password" data-bind="value: ftpPass" />') })}
                                ${LpFormField({ label: 'FTP Directory', children: raw('<input class="edit-input" type="text" data-bind="value: ftpDir" />') })}
                                ${LpFormField({ label: 'PGP Enabled', children: raw(this.gpgEnabledSelect.getHtmlBuild()) })}
                                ${LpFormField({ label: 'Keyring Password', children: raw('<input class="edit-input" type="password" data-bind="value: gpgKeyringPassword" />') })}
                                ${LpFormField({ label: 'Output Filename', children: raw('<input class="edit-input" type="text" data-bind="value: gpgOutputFilename" />') })}
                                ${LpFormField({ label: 'Sign With', children: raw('<input class="edit-input" type="text" data-bind="value: gpgSignWith" />') })}
                            `,
            })}
                        <div class="admin-transmission-recipients">
                            <table class="list-results">
                                <thead><tr><th>GPG Recipient ID</th><th>Recipient</th></tr></thead>
                                <tbody data-bind="foreach: gpgRecipients"><tr><td data-bind="text: id"></td><td data-bind="text: recipient"></td></tr></tbody>
                            </table>
                        </div>
                    `,
        })}

                ${LpEditCard({
            title: 'Audit information',
            description: 'Read-only metadata for this transmission.',
            attributes: {
                'data-bind': "visible: activeSection() === 'audit'",
            },
            children: LpEditGrid({
                children: raw(`
                            <div><strong>ID:</strong> <span data-bind="text: id"></span></div>
                            <div><strong>Label:</strong> <span data-bind="text: name"></span></div>
                            <div><strong>Channel:</strong> <span data-bind="text: channel"></span></div>
                            <div><strong>Type:</strong> <span data-bind="text: transmissionType"></span></div>
                            <div><strong>Live:</strong> <span data-bind="text: live"></span></div>
                            <div><strong>Last Updated:</strong> <span data-bind="text: lastUpdatedDate"></span></div>
                            <div><strong>Updated By:</strong> <span data-bind="text: lastUpdatedBy"></span></div>
                        `),
            }),
        })}

                ${LpEditSteps({})}
                </div>
            </div>
        `;
    }
    /**
     * Apply reference data returned by the API to the view model.
     *
     * @param references any
     * @return void
     */
    setReferences(references) {
        const vm = this.getTemplateViewModel();
        const types = references.types || [];
        const channels = references.channels || [];
        const agencies = references.agencies || [];
        vm.types(types);
        vm.channels(channels);
        vm.groups(references.groups || []);
        vm.partners(references.partnerReferences && references.partnerReferences.partners ? references.partnerReferences.partners : []);
        vm.customizationPresets(references.customizationPresets || []);
        this.channelSelect.setOptions([
            { id: '', name: 'Select channel' },
            ...this.toSelectOptions(channels),
        ]);
        this.typeSelect.setOptions([
            { id: '', name: 'Select type' },
            ...this.toSelectOptions(types),
        ]);
        this.presetSelect.setOptions(this.toSelectOptions(vm.availableCustomizationPresets() || []));
        this.setAgencies(agencies);
    }
    /**
     * Apply agency options for the current channel.
     */
    setAgencies(agencies) {
        const vm = this.getTemplateViewModel();
        const normalized = this.toSelectOptions(agencies || []);
        vm.agencies(normalized);
        this.agencySelect.setOptions([
            { id: '', name: 'Select agency' },
            ...normalized,
        ]);
        const currentAgencyId = String(vm.transmissionAgencyId() || '');
        if (currentAgencyId && !normalized.some((agency) => agency.id === currentAgencyId)) {
            vm.transmissionAgencyId('');
        }
    }
    /**
     * Create a form-bound single-select control (Partners Status UI, no list reload).
     */
    createFormSelect(id, label, valueBinding, options) {
        const select = new listPageSelect_template(id, this.getTemplateViewModel(), this.caller);
        select.setLabel(label);
        select.setFormField(true);
        if (options) {
            select.setOptions(options);
        }
        select.bindValue(valueBinding);
        return select;
    }
    /**
     * Normalize select options so ids always match listPageSelect string values.
     */
    toSelectOptions(options) {
        return (options || []).map((option) => ({
            id: option && option.id !== undefined && option.id !== null ? String(option.id) : '',
            name: String((option && (option.name || option.label)) || ''),
        })).filter((option) => option.id !== '' || option.name !== '');
    }
    /**
     * Coerce a value to the string form expected by listPageSelect.
     */
    asSelectValue(value, fallback = '') {
        if (value === null || typeof value === 'undefined' || value === '') {
            return fallback;
        }
        return String(value);
    }
    /**
     * Apply a transmission DTO to the edit view model.
     *
     * @param transmission any
     * @return void
     */
    setTransmission(transmission) {
        const vm = this.getTemplateViewModel();
        Object.keys(vm).forEach((key) => {
            if (typeof vm[key] === 'function' && typeof vm[key].peek === 'function' && transmission[key] !== undefined) {
                vm[key](transmission[key]);
            }
        });
        vm.id(Number(transmission.id || 0));
        vm.name(transmission.name || transmission.label || '');
        vm.channel(this.asSelectValue(transmission.channel, 'REC'));
        vm.transmissionAgencyId(this.asSelectValue(transmission.transmissionAgencyId));
        vm.transmissionType(this.asSelectValue(transmission.transmissionType));
        vm.live(this.asSelectValue(transmission.live !== undefined ? transmission.live : 1, '1'));
        vm.groovySource(this.asSelectValue(transmission.groovySource, 'NONE'));
        vm.showHeaders(this.asSelectValue(transmission.showHeaders, '0'));
        vm.zipEnabled(this.asSelectValue(transmission.zipEnabled, '0'));
        vm.trackChanges(this.asSelectValue(transmission.trackChanges, '0'));
        vm.forceSendingEmail(this.asSelectValue(transmission.forceSendingEmail, '0'));
        vm.isSecure(this.asSelectValue(transmission.isSecure, '0'));
        vm.sendLeadId(this.asSelectValue(transmission.sendLeadId, '0'));
        vm.isEncoded(this.asSelectValue(transmission.isEncoded, '0'));
        vm.sftpEnabled(this.asSelectValue(transmission.sftpEnabled, '0'));
        vm.gpgEnabled(this.asSelectValue(transmission.gpgEnabled, '0'));
        vm.partnerIdsText((transmission.partnerIds || []).join(', '));
        vm.groupIdsText((transmission.transmissionGroups || []).map((group) => group.id || group).join(', '));
        vm.gpgRecipients(transmission.gpgRecipients || []);
        const selectedPreset = String(vm.groovySource() || 'NONE');
        const knownPreset = (vm.customizationPresets() || []).some((preset) => String(preset.id) === selectedPreset);
        if (!selectedPreset || !knownPreset) {
            vm.groovySource('NONE');
        }
        this.ensureActiveSectionVisible();
    }
    /**
     * Build the save payload from the current view model state.
     *
     * @return any
     */
    getPayload() {
        const vm = this.getTemplateViewModel();
        const readIds = function (value) {
            return (value || '').split(/[\s,]+/).map((item) => Number(item)).filter((item) => item > 0);
        };
        const presetId = (vm.availableCustomizationPresets() || []).some((preset) => String(preset.id) === String(vm.groovySource() || ''))
            ? vm.groovySource()
            : 'NONE';
        return {
            id: Number(vm.id() || 0),
            name: vm.name(),
            channel: vm.channel(),
            transmissionAgencyId: Number(vm.transmissionAgencyId() || 0) || null,
            partnerIds: readIds(vm.partnerIdsText()),
            transmissionType: vm.transmissionType(),
            transmissionGroups: readIds(vm.groupIdsText()).map((id) => ({ id })),
            live: Number(vm.live() || 0),
            sqlstatement: vm.sqlstatement(),
            tokenizedParameters: vm.tokenizedParameters(),
            maxLeadsPerTransmission: Number(vm.maxLeadsPerTransmission() || 0) || null,
            groovySource: presetId,
            sftpEnabled: Number(vm.sftpEnabled() || 0),
            ftpHost: vm.ftpHost(),
            ftpUsername: vm.ftpUsername(),
            ftpPass: vm.ftpPass(),
            ftpDir: vm.ftpDir(),
            gpgEnabled: Number(vm.gpgEnabled() || 0),
            gpgKeyringPassword: vm.gpgKeyringPassword(),
            gpgOutputFilename: vm.gpgOutputFilename(),
            gpgSignWith: vm.gpgSignWith(),
            reportType: vm.reportType(),
            reportTemplateFile: vm.reportTemplateFile(),
            trackChanges: Number(vm.trackChanges() || 0),
            forceSendingEmail: Number(vm.forceSendingEmail() || 0),
            filename: vm.filename(),
            fileExtension: vm.fileExtension(),
            fieldDelimiter: vm.fieldDelimiter(),
            eolDelimiter: vm.eolDelimiter(),
            showHeaders: Number(vm.showHeaders() || 0),
            zipEnabled: Number(vm.zipEnabled() || 0),
            fileHeader: vm.fileHeader(),
            fileFooter: vm.fileFooter(),
            isSecure: Number(vm.isSecure() || 0),
            sendLeadId: Number(vm.sendLeadId() || 0),
            isEncoded: Number(vm.isEncoded() || 0),
            hostname: vm.hostname(),
            hostpath: vm.hostpath(),
            port: Number(vm.port() || 0) || null,
            successStrings: vm.successStrings(),
            errorStrings: vm.errorStrings(),
            httpRequestHeaders: vm.httpRequestHeaders(),
            emailFrom: vm.emailFrom(),
            emailTo: vm.emailTo(),
            emailCc: vm.emailCc(),
            emailBcc: vm.emailBcc(),
            subject: vm.subject(),
            contentType: vm.contentType(),
            contentMessage: vm.contentMessage(),
            comments: vm.comments(),
        };
    }
    /**
     * Activate a transmission section in the sidebar.
     *
     * @param sectionId string
     * @return void
     */
    selectSection(sectionId) {
        this.getTemplateViewModel().activeSection(sectionId);
    }
    /**
     * Move to the previous visible section.
     *
     * @return void
     */
    goToPreviousSection() {
        const vm = this.getTemplateViewModel();
        const index = this.getSectionIndex(vm.activeSection());
        if (index <= 0) {
            return;
        }
        vm.activeSection(this.getVisibleSectionIds()[index - 1]);
    }
    /**
     * Validate the current section and move to the next one for new transmissions.
     *
     * @return void
     */
    goToNextSection() {
        const vm = this.getTemplateViewModel();
        const currentSection = vm.activeSection();
        if (!vm.id() && !this.validateSection(currentSection)) {
            return;
        }
        const index = this.getSectionIndex(currentSection);
        const ids = this.getVisibleSectionIds();
        if (index >= ids.length - 1) {
            return;
        }
        vm.activeSection(ids[index + 1]);
    }
    /**
     * Return visible sections for the current transmission type and mode.
     *
     * @return Array<{ id: string, label: string, icon: string, title: string, description: string }>
     */
    getVisibleSections() {
        const vm = this.getTemplateViewModel();
        const transmissionType = String(vm.transmissionType() || '');
        const isEdit = Number(vm.id() || 0) > 0;
        return this.sections.filter((section) => this.isSectionVisible(section.id, transmissionType, isEdit));
    }
    /**
     * Return ordered visible section ids.
     *
     * @return Array<string>
     */
    getVisibleSectionIds() {
        return this.getVisibleSections().map((section) => section.id);
    }
    /**
     * Return the index of a section id within visible sections.
     *
     * @param sectionId string
     * @return number
     */
    getSectionIndex(sectionId) {
        return this.getVisibleSectionIds().indexOf(sectionId);
    }
    /**
     * Keep the active section aligned with the visible sidebar items.
     *
     * @return void
     */
    ensureActiveSectionVisible() {
        const vm = this.getTemplateViewModel();
        const ids = this.getVisibleSectionIds();
        if (!ids.length) {
            return;
        }
        if (ids.indexOf(vm.activeSection()) === -1) {
            vm.activeSection(ids[0]);
        }
    }
    /**
     * Return whether a section should appear in the sidebar.
     *
     * @param sectionId string
     * @param transmissionType string
     * @param isEdit boolean
     * @return boolean
     */
    isSectionVisible(sectionId, transmissionType, isEdit) {
        if (sectionId === 'audit') {
            return isEdit;
        }
        if (sectionId === 'basics' || sectionId === 'targeting' || sectionId === 'preset') {
            return true;
        }
        if (!transmissionType) {
            return false;
        }
        if (sectionId === 'file' || sectionId === 'security') {
            return this.isFileDeliveryType(transmissionType);
        }
        if (sectionId === 'report') {
            return transmissionType === 'REPORT_BY_EMAIL';
        }
        if (sectionId === 'http') {
            return ['LEAD_HTTP_POST', 'LEAD_XML_POST', 'LEAD_POST_FILE'].indexOf(transmissionType) !== -1;
        }
        if (sectionId === 'email') {
            return ['LEAD_EMAIL', 'LEAD_FILE_BY_EMAIL', 'REPORT_BY_EMAIL'].indexOf(transmissionType) !== -1;
        }
        return false;
    }
    /**
     * Return whether the transmission type uses file-based delivery settings.
     *
     * @param transmissionType string
     * @return boolean
     */
    isFileDeliveryType(transmissionType) {
        return [
            'LEAD_POST_FILE',
            'LEAD_FILE_BY_EMAIL',
            'REPORT_BY_EMAIL',
            'LEAD_FILE',
            'LEAD_BOX_FILE_UPLOAD',
        ].indexOf(transmissionType) !== -1;
    }
    /**
     * Validate required fields for a section on new transmissions.
     *
     * @param sectionId string
     * @return boolean
     */
    validateSection(sectionId) {
        const vm = this.getTemplateViewModel();
        if (sectionId === 'basics') {
            if (!String(vm.name() || '').trim()) {
                alert('Transmission name is required.');
                return false;
            }
            if (!String(vm.transmissionType() || '').trim()) {
                alert('Transmission type is required.');
                return false;
            }
        }
        return true;
    }
    /**
     * Return section completion flags for sidebar indicators.
     *
     * @return Record<string, boolean>
     */
    getSectionCompletion() {
        const vm = this.getTemplateViewModel();
        return {
            basics: !!(String(vm.name() || '').trim() && String(vm.transmissionType() || '').trim()),
            targeting: !!(String(vm.partnerIdsText() || '').trim() || String(vm.groupIdsText() || '').trim() || String(vm.sqlstatement() || '').trim()),
            preset: String(vm.groovySource() || 'NONE') !== 'NONE',
            file: !!(String(vm.filename() || '').trim() || String(vm.fileExtension() || '').trim()),
            report: !!(String(vm.reportType() || '').trim() || String(vm.reportTemplateFile() || '').trim()),
            http: !!(String(vm.hostname() || '').trim() || String(vm.hostpath() || '').trim()),
            email: !!(String(vm.emailTo() || '').trim() || String(vm.subject() || '').trim()),
            security: Number(vm.sftpEnabled() || 0) === 1 || Number(vm.gpgEnabled() || 0) === 1,
            audit: Number(vm.id() || 0) > 0,
        };
    }
}
